/**
 * Normalizes various Unicode hyphen characters to the standard ASCII hyphen-minus character.
 *
 * This function replaces different hyphen variants (including non-breaking hyphens, soft hyphens,
 * en dashes, em dashes, fullwidth hyphens, etc.) with the standard hyphen-minus character (-).
 *
 * @param str - The input string containing potential hyphen variants
 * @returns A new string with all hyphen variants replaced by standard hyphens
 *
 * @example
 * ```typescript
 * normalizeHyphen('hello‐world'); // returns 'hello-world' (non-breaking hyphen)
 * normalizeHyphen('foo­bar'); // returns 'foo-bar' (soft hyphen replaced)
 * normalizeHyphen('240477–pos1-1'); // returns '240477-pos1-1' (en dash replaced)
 * ```
 */
export const normalizeHyphen = (str: string): string => {
  // Map of hyphen variants to replace with standard hyphen-minus
  const hyphenVariants: Record<string, string> = {
    '\u2010': '-', // hyphen
    '\u2011': '-', // non-breaking hyphen
    '\u2013': '-', // en dash
    '\u2014': '-', // em dash
    '\u00AD': '-', // soft hyphen
    '\uFF0D': '-', // fullwidth hyphen-minus
    '\uFE63': '-', // small hyphen-minus
    '\u1806': '-', // mongolian todo soft hyphen
    '\u2043': '-', // hyphen bullet
  }

  // Create regex pattern from all hyphen variants
  const pattern = new RegExp(
    '[' + Object.keys(hyphenVariants).join('') + ']',
    'g'
  )

  // Replace all variants with standard hyphen
  return str.replace(pattern, '-')
}

/**
 * Applies a series of string transformation functions to normalize a string.
 *
 * @param str - The input string to be normalized
 * @param funcs - An array of transformation functions to be applied sequentially
 * @returns The normalized string after all transformations have been applied
 *
 * @example
 * ```typescript
 * const result = normalizeString("  Hello-World  ", [
 *   (s) => s.toLowerCase(),
 *   (s) => s.trim(),
 *   (s) => s.replace(/-/g, '_')
 * ]);
 * // Returns: "hello_world"
 *
 * // Using built-in utility functions
 * const result2 = normalizeString("  HELLO‐WORLD  ", [
 *   toLowerCase,
 *   trim,
 *   normalizeHyphen
 * ]);
 * // Returns: "hello-world"
 * ```
 */
export const normalizeString = (
  str: string,
  funcs: ReadonlyArray<(s: string) => string>
): string => {
  if (!str) return str
  if (!funcs || funcs.length === 0) return str

  return funcs.reduce((acc, func) => {
    if (typeof func !== 'function') {
      throw new Error('All items in funcs array must be functions')
    }
    return func(acc)
  }, str)
}

/**
 * Calculates the Levenshtein distance between two strings.
 *
 * The Levenshtein distance is a string metric for measuring the difference between two sequences.
 * It represents the minimum number of single-character edits (insertions, deletions, or substitutions)
 * required to change one string into another.
 *
 * This implementation uses dynamic programming with a 2D matrix to efficiently compute the distance
 * in O(m*n) time complexity, where m and n are the lengths of the input strings.
 *
 * @param a - The first string to compare
 * @param b - The second string to compare
 * @returns The Levenshtein distance as a non-negative integer. Returns 0 if strings are identical.
 *
 * @example
 * ```typescript
 * levenshtein('kitten', 'sitting'); // returns 3
 * // Changes: k→s, e→i, insert g
 *
 * levenshtein('hello', 'hello'); // returns 0 (identical strings)
 *
 * levenshtein('', 'abc'); // returns 3 (insert 3 characters)
 *
 * levenshtein('abc', ''); // returns 3 (delete 3 characters)
 *
 * levenshtein('cat', 'dog'); // returns 3 (substitute all 3 characters)
 *
 * // Useful for fuzzy string matching and spell checking
 * const similarity = 1 - (levenshtein('color', 'colour') / Math.max('color'.length, 'colour'.length));
 * // similarity ≈ 0.83 (83% similar)
 * ```
 * This function can be used for data match feature.
 * e.g. The search input is 05/02/2024, and the return values are 05/02/2024, and Date: 05/02/2024
 * using levenshtein to calculate the distance between search input and return values,
 * return value with the lowest distance is the best match.
 * @see {@link https://en.wikipedia.org/wiki/Levenshtein_distance} for algorithm details
 */
export const levenshtein = (a: string, b: string): number => {
  const an = a ? a.length : 0
  const bn = b ? b.length : 0
  if (an === 0) {
    return bn
  }
  if (bn === 0) {
    return an
  }
  const matrix = new Array<number[]>(bn + 1)
  for (let i = 0; i <= bn; ++i) {
    let row = (matrix[i] = new Array<number>(an + 1))
    row[0] = i
  }
  const firstRow = matrix[0]
  for (let j = 1; j <= an; ++j) {
    firstRow[j] = j
  }
  for (let i = 1; i <= bn; ++i) {
    for (let j = 1; j <= an; ++j) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] =
          Math.min(
            matrix[i - 1][j - 1], // substitution
            matrix[i][j - 1], // insertion
            matrix[i - 1][j] // deletion
          ) + 1
      }
    }
  }
  return matrix[bn][an]
}
